import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, Link } from 'react-router-dom';
import gsap from 'gsap';
import clsx from 'clsx';
import LogoIcon from '../../assets/logo.svg';
import { useSelector } from 'react-redux';
import { selectUserRoles } from '../../features/auth/authSlice';
import { useAdminListKycQuery } from '../../features/kyc/kycApi';
import { useListAdminTicketsQuery, useListMyTicketsQuery } from '../../features/tickets/ticketsApi';
import {
  HomeIcon,
  UsersIcon,
  ShieldCheckIcon,
  LayersIcon,
  TrendingUpIcon,
  MessageCircleIcon,
  FileTextIcon,
  ShieldIcon,
  ClockIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XIcon,
} from '../ui/icons';

const COLLAPSE_STORAGE_KEY = 'cavan_sidebar_collapsed';

// Order matters here too — live, real-backend sections first, "coming
// soon" stub sections after. Support and Reports went live in v1.14.0;
// Statements still has no real endpoint. `badgeKey` looks up a live count
// from the small badgeCounts map built below (open tickets / pending KYC),
// left undefined for links with no count to show.
const investorLinks = [
  { to: '/dashboard', label: 'Dashboard', icon: HomeIcon },
  { to: '/kyc', label: 'KYC Verification', icon: ShieldCheckIcon },
  { to: '/plans', label: 'Investment Plans', icon: LayersIcon },
  { to: '/my-investments', label: 'My Investments', icon: TrendingUpIcon },
  { to: '/tickets', label: 'Support', icon: MessageCircleIcon, badgeKey: 'myOpenTickets' },
  { to: '/reports', label: 'Reports', icon: FileTextIcon },
  { to: '/statements', label: 'Statements', icon: FileTextIcon, soon: true },
];

// Order matters: live, real-backend sections first, "coming soon" stub
// sections after — so the nav itself reflects what's actually there.
const adminLinks = [{ to: '/admin', label: 'Overview', icon: HomeIcon }];

const liveAdminLinks = [
  { to: '/admin/investors', label: 'Investors', icon: UsersIcon },
  { to: '/admin/investments', label: 'Investments', icon: TrendingUpIcon },
  { to: '/admin/unlock-account', label: 'Unlock Account', icon: ShieldIcon },
  { to: '/admin/plans', label: 'Investment Plans', icon: LayersIcon },
  { to: '/admin/kyc', label: 'KYC Review', icon: ShieldCheckIcon, badgeKey: 'pendingKyc' },
  // v1.18.0 — Template Management. Gated the same as every other entry in
  // this array (admin OR super-admin) — there's no more granular
  // `manage-kyc-documents` signal available client-side to gate on
  // specifically (see selectUserRoles), same constraint as manage-investments.
  { to: '/admin/kyc-templates', label: 'KYC Templates', icon: FileTextIcon },
  { to: '/admin/onboard-client', label: 'Onboard Client', icon: UsersIcon },
  { to: '/admin/tickets', label: 'Support Tickets', icon: MessageCircleIcon, badgeKey: 'adminOpenTickets' },
  { to: '/admin/reports', label: 'Reports', icon: FileTextIcon },
];

// Super-admin exclusive — gated below by only ever spreading this array in
// when isSuperAdmin. Audit Logs lives here (not in liveAdminLinks) per the
// backend's access rule: it's checked against the super-admin ROLE
// specifically, stricter than every other admin-tagged endpoint, so a plain
// admin must never even see the nav entry, not just a disabled one.
const superAdminLinks = [
  { to: '/admin/admins', label: 'Admin Accounts', icon: ShieldIcon },
  { to: '/admin/audit-logs', label: 'Audit Logs', icon: ClockIcon },
];

function NavBadge({ count }) {
  if (!count) return null;
  return (
    <span className="ml-auto shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-warning text-[10px] font-semibold text-white flex items-center justify-center">
      {count > 99 ? '99+' : count}
    </span>
  );
}

function SidebarNav({ links, badgeCounts, collapsed, onNavigate }) {
  return (
    <nav className="flex flex-col gap-1">
      {links.map((link) => (
        <NavLink
          key={link.to}
          to={link.to}
          end={link.to === '/admin'}
          onClick={onNavigate}
          title={collapsed ? link.label : undefined}
          className={({ isActive }) =>
            clsx(
              'group relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200',
              isActive
                ? 'bg-accent text-white shadow-glow-sm'
                : 'text-sidebar-muted hover:text-sidebar-foreground hover:bg-white/10'
            )
          }
        >
          <link.icon className="h-5 w-5 shrink-0" />
          <span
            className={clsx(
              'overflow-hidden whitespace-nowrap transition-all duration-200 ease-out',
              collapsed ? 'max-w-0 opacity-0' : 'max-w-[160px] opacity-100'
            )}
          >
            {link.label}
          </span>
          {!collapsed && link.badgeKey && <NavBadge count={badgeCounts[link.badgeKey]} />}
          {!collapsed && link.soon && (
            <span className="ml-auto shrink-0 text-[10px] font-medium text-sidebar-muted bg-white/10 px-1.5 py-0.5 rounded-full">
              Soon
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

/**
 * Dark rail (per the design system's "dark sidebar for contrast on a light
 * content area" rule — see the `sidebar-*` tokens in tailwind.config.js,
 * used only here). Desktop: docked open by default, collapses to an
 * icon-only rail via a toggle at the bottom, state persisted in
 * localStorage so it survives navigation and reloads. Mobile: unchanged
 * off-canvas overlay behavior, just restyled to match — always shows full
 * labels since there's no rail concept on a phone.
 */
export default function Sidebar({ open, onClose }) {
  const roles = useSelector(selectUserRoles);
  const isAdmin = roles.includes('admin') || roles.includes('super-admin');
  const isSuperAdmin = roles.includes('super-admin');
  const isInvestor = !isAdmin;

  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(COLLAPSE_STORAGE_KEY) === '1';
  });

  useEffect(() => {
    window.localStorage.setItem(COLLAPSE_STORAGE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  // Small, cheap live counts for nav badges — every hook is called
  // unconditionally (Rules of Hooks) and skipped via role rather than
  // branching the hook call itself. per_page: 1 keeps the payload tiny;
  // only meta.total is read.
  const { data: kycData } = useAdminListKycQuery({ status: 'pending', page: 1, per_page: 1 }, { skip: !isAdmin });
  const { data: adminTicketsData } = useListAdminTicketsQuery({ status: 'open', page: 1, per_page: 1 }, { skip: !isAdmin });
  const { data: myTicketsData } = useListMyTicketsQuery({ status: 'open', page: 1, per_page: 1 }, { skip: !isInvestor });

  const badgeCounts = {
    pendingKyc: kycData?.meta?.total ?? 0,
    adminOpenTickets: adminTicketsData?.meta?.total ?? 0,
    myOpenTickets: myTicketsData?.meta?.total ?? 0,
  };

  const links = isAdmin
    ? [...adminLinks, ...(isSuperAdmin ? superAdminLinks : []), ...liveAdminLinks]
    : investorLinks;

  const overlayRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const ctx = gsap.context(() => {
      gsap.fromTo(overlayRef.current, { opacity: 0 }, { opacity: 1, duration: 0.2 });
      gsap.fromTo(panelRef.current, { x: '-100%' }, { x: '0%', duration: 0.32, ease: 'power3.out' });
    });

    const onKeyDown = (e) => e.key === 'Escape' && onClose?.();
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      ctx.revert();
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  return (
    <>
      {/* Desktop: docked rail, sticky to the viewport so it never scrolls
          with the page — only its own nav list (below) scrolls internally
          when the link list is taller than the screen. Width animates
          between full and icon-only. */}
      <aside
        className={clsx(
          'hidden md:flex md:flex-col shrink-0 relative z-10 sticky top-0 h-screen py-6 shadow-sidebar transition-all duration-200 ease-out',
          'bg-gradient-to-b from-[#0E1826]/92 via-[#0A121C]/90 to-[#122036]/92 backdrop-blur-2xl border border-white/10',
          collapsed ? 'w-[72px] px-3' : 'w-64 px-4'
        )}
      >
        {/* Watery glass sheen — soft emerald/cyan glows drifting behind the
            translucent panel, purely decorative, sits below all content. */}
        <div
          className="pointer-events-none absolute inset-0 z-0"
          aria-hidden="true"
          style={{
            background:
              'radial-gradient(120% 55% at 15% 0%, rgba(91, 140, 181, 0.20), transparent 60%), radial-gradient(110% 60% at 100% 100%, rgba(139, 175, 209, 0.14), transparent 55%)',
          }}
        />

        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="absolute z-30 right-0 top-7 h-6 w-6 flex items-center justify-center rounded-full  backdrop-blur-md text-sidebar-muted hover:text-sidebar-foreground hover:bg-white/20 shadow-sidebar ring-1 ring-white/15 transition-colors"
        >
          {collapsed ? <ChevronRightIcon className="h-3.5 w-3.5" /> : <ChevronLeftIcon className="h-3.5 w-3.5" />}
        </button>

        <Link
          to="/"
          className={clsx('relative z-10 flex items-center gap-2 mb-8', collapsed ? 'px-0.5 justify-center' : 'px-2')}
        >
          <img src={LogoIcon} alt="" className="h-8 w-8 rounded-lg shrink-0 shadow-glow-sm" />
          <span
            className={clsx(
              'overflow-hidden whitespace-nowrap font-semibold text-sidebar-foreground transition-all duration-200 ease-out',
              collapsed ? 'max-w-0 opacity-0' : 'max-w-[160px] opacity-100'
            )}
          >
            Cavan Capital
          </span>
        </Link>

        <div className="relative z-10 flex-1 overflow-y-auto">
          <SidebarNav links={links} badgeCounts={badgeCounts} collapsed={collapsed} />
        </div>
      </aside>

      {/* Mobile: off-canvas drawer, portaled so it overlays the whole app. */}
      {open &&
        createPortal(
          <div className="fixed inset-0 z-50 md:hidden">
            <div
              ref={overlayRef}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={onClose}
              aria-hidden="true"
            />
            <div
              ref={panelRef}
              role="dialog"
              aria-modal="true"
              aria-label="Navigation menu"
              className="absolute inset-y-0 left-0 w-72 max-w-[85vw] overflow-hidden bg-gradient-to-b from-[#0E1826]/92 via-[#0A121C]/90 to-[#122036]/92 backdrop-blur-2xl border-r border-white/10 shadow-sidebar px-4 py-6 flex flex-col"
            >
              <div
                className="pointer-events-none absolute inset-0 z-0"
                aria-hidden="true"
                style={{
                  background:
                    'radial-gradient(120% 55% at 15% 0%, rgba(91, 140, 181, 0.20), transparent 60%), radial-gradient(110% 60% at 100% 100%, rgba(139, 175, 209, 0.14), transparent 55%)',
                }}
              />
              <div className="relative z-10 flex items-center justify-between mb-8 px-2">
                <Link to="/" onClick={onClose} className="flex items-center gap-2">
                  <img src={LogoIcon} alt="" className="h-8 w-8 rounded-lg" />
                  <span className="font-semibold text-sidebar-foreground">Cavan Capital</span>
                </Link>
                <button
                  onClick={onClose}
                  aria-label="Close menu"
                  className="h-9 w-9 flex items-center justify-center rounded-lg text-sidebar-muted hover:text-sidebar-foreground hover:bg-white/10 transition-colors"
                >
                  <XIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="relative z-10 flex-1 overflow-y-auto">
                <SidebarNav links={links} badgeCounts={badgeCounts} collapsed={false} onNavigate={onClose} />
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
